import axios from 'axios'

import { SnackbarService } from '../../shared/snackbar/snackbar.service'
import { useAppConfigStore } from '../../shared/store/app-config.store'

type CountAllData = {
  genreCount: number
  infoPratiqueCount: number
  infoPratiqueSectionCount: number
  musicGroupCount: number
  programCount: number
  programEventCount: number
  subEventCount: number
  sceneCount: number
  sponsorCount: number
  sponsorCategoryCount: number
  poiCount: number
  markerIconsCount: number
  zoiCount: number
  typeCounts: Record<string, number>
}

export type ExtractRequest = {
  message: string
  askedBy: string
  date?: number
  latest?: boolean
  version: number
  api: number
  edition: number
  publishDate?: number
  datas?: Record<string, unknown>
  dataCounts?: CountAllData
  error?: string
  stackTrace?: string
  gedFileExportId?: string
  _id?: string
  status?: ExtractStatus
}

export type PaginatedExtracts = {
  items: ExtractRequest[]
  total: number
}

export const ExtractStatus = {
  QUEUED: 'QUEUED',
  IN_PROGRESS: 'IN_PROGRESS',
  EXTRACTED: 'EXTRACTED',
  PUBLISHED: 'PUBLISHED',
  FAILED_EXTRACTION: 'FAILED_EXTRACTION',
  FAILED_PUBLICATION: 'FAILED_PUBLICATION',
} as const
export type ExtractStatus = (typeof ExtractStatus)[keyof typeof ExtractStatus]

export const PUBLICATIONS_POLLING_INTERVAL_MS = 5000

/** The list is refreshed while an export is queued or running, or while a requested export has not shown up yet. */
export function shouldPollPublications(
  publications: ExtractRequest[],
  isExportPending: boolean,
): boolean {
  return isExportPending || publications.some(isPendingExtract)
}

export function isPendingExtract(item: ExtractRequest): boolean {
  return (
    item.status === ExtractStatus.IN_PROGRESS ||
    item.status === ExtractStatus.QUEUED
  )
}

export const PublicationsService = {
  async getAll(page = 0, size = 20): Promise<PaginatedExtracts> {
    const { data } = await axios.get<PaginatedExtracts>('/api/exports', {
      params: { page, size },
    })
    if (data.items.length > 0 && page === 0) {
      data.items[0].latest = true
    }
    return data
  },
  async getDiff(versionNumber: number): Promise<ExtractRequest> {
    const { data } = await axios.get<ExtractRequest>(
      `/api/export/${versionNumber}`,
    )
    return data
  },

  async publishPublication(): Promise<void> {
    try {
      await axios.post('/api/export/publish')
      SnackbarService.info('Les données ont bien été publiées')
    } catch {
      SnackbarService.error('Une erreur est survenue')
    }
  },

  async cancelExport(id: string): Promise<void> {
    try {
      await axios.delete(`/api/exports/${id}`)
      SnackbarService.info("L'export en attente a été annulé")
    } catch (error) {
      SnackbarService.error("Impossible d'annuler cet export")
      throw error
    }
  },

  async doPublication(message: { message: string }): Promise<void> {
    try {
      await axios.post('/api/exports', message)
      await useAppConfigStore.getState().loadAppConfig()
      SnackbarService.info("L'export est en cours de génération.")
    } catch (error: unknown) {
      SnackbarService.error("Oups, l'export n'a pas pu être planifié")
      throw error
    }
  },
}
